/**
 * THE SHARE PANEL — four ways to hand the list to a person.
 *
 * It opens IN THE PAGE under the Share button (no modal, nothing to dismiss)
 * and it works the same on a phone and on an iPad.
 *
 * NOTHING IS SENT FROM OUR SERVERS. "Text message" and "Email" hand the ready
 * message to the phone's own share sheet, or to its messages and mail apps
 * through sms: and mailto:. The shopper presses send.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Link2, Loader2, Mail, MessageSquare, ShieldOff, X } from 'lucide-react';

const copy = async (text) => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_e) { /* fall through */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch (_e) { return false; }
};

const Action = ({ icon: Icon, label, onClick, testId, disabled }) => (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
        className="flex min-h-[56px] flex-1 basis-[46%] items-center gap-2 rounded-2xl border-2 border-slate-200 px-3 text-left text-sm font-bold text-foreground transition-colors hover:border-emerald-500 disabled:opacity-50 dark:border-slate-700">
        <Icon className="h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300" />
        <span className="min-w-0">{label}</span>
    </button>
);

export default function GrocerySharePanel({ api, session, lang, onClose }) {
    const { t } = useTranslation();
    const [state, setState] = useState(null);
    const [share, setShare] = useState(null);     // { link, message, subject, expires_at }
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);

    const prepare = useCallback(async () => {
        setFailed(false);
        setBusy(true);
        try {
            const st = await api('/share/state', { session, lang });
            setState(st);
            if (st.enabled) setShare(await api('/share/link', { method: 'POST', session, lang }));
        } catch (e) {
            setFailed(String(e.message).includes('429') || String(e.message).includes('rate')
                ? 'rate' : true);
        } finally { setBusy(false); }
    }, [api, session, lang]);

    useEffect(() => { prepare(); }, [prepare]);

    const byText = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ text: share.message });
                return;
            } catch (_e) { return; }            // the shopper closed the sheet
        }
        window.location.href = `sms:?&body=${encodeURIComponent(share.message)}`;
    };

    const byEmail = () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(share.subject)}`
            + `&body=${encodeURIComponent(share.message)}`;
    };

    const copyLink = async () => {
        const ok = await copy(share.link);
        if (ok) toast.success(t('grocery.sharing.linkCopied'));
        else toast.error(t('grocery.couldNotLoad'));
    };

    const copyText = async () => {
        const ok = await copy(share.message);
        if (ok) toast.success(t('grocery.sharing.textCopied'));
        else toast.error(t('grocery.couldNotLoad'));
    };

    const stop = async () => {
        setBusy(true);
        try {
            await api('/share/stop', { method: 'POST', session, lang });
            setShare(null);
            setState((s) => ({ ...(s || {}), sharing: false }));
            toast.success(t('grocery.sharing.stopped'));
        } catch (_e) {
            toast.error(t('grocery.couldNotLoad'));
        } finally { setBusy(false); }
    };

    const until = share?.expires_at
        ? new Date(share.expires_at).toLocaleDateString(lang, { day: 'numeric', month: 'long' })
        : '';

    return (
        <div className="space-y-3 rounded-2xl border-2 border-emerald-200 bg-white p-4 dark:border-emerald-500/30 dark:bg-slate-900"
            data-testid="grocery-share-panel">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-bold text-foreground">{t('grocery.sharing.title')}</p>
                    <p className="text-xs text-muted-foreground">{t('grocery.sharing.subtitle')}</p>
                </div>
                <button onClick={onClose} aria-label={t('grocery.close')}
                    data-testid="grocery-share-close"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10">
                    <X className="h-4 w-4" />
                </button>
            </div>

            {busy && !share ? (
                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />{t('grocery.sharing.preparing')}
                </div>
            ) : null}

            {failed === 'rate' ? (
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300"
                    data-testid="grocery-share-rate-limited">{t('grocery.sharing.tooMany')}</p>
            ) : null}
            {failed === true ? (
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground"
                        data-testid="grocery-share-failed">{t('grocery.couldNotLoad')}</p>
                    <button onClick={prepare} data-testid="grocery-share-retry"
                        className="min-h-[44px] text-sm font-bold text-emerald-700 underline dark:text-emerald-300">
                        {t('grocery.tryAgain')}
                    </button>
                </div>
            ) : null}
            {state && !state.enabled ? (
                <p className="text-sm text-muted-foreground" data-testid="grocery-share-off">
                    {t('grocery.sharing.notAvailable')}
                </p>
            ) : null}

            {share ? (
                <>
                    <div className="flex flex-wrap gap-2">
                        <Action icon={MessageSquare} label={t('grocery.sharing.byText')}
                            onClick={byText} testId="grocery-share-sms" />
                        <Action icon={Mail} label={t('grocery.sharing.byEmail')}
                            onClick={byEmail} testId="grocery-share-email" />
                        <Action icon={Link2} label={t('grocery.sharing.copyLink')}
                            onClick={copyLink} testId="grocery-share-copy-link" />
                        <Action icon={Copy} label={t('grocery.sharing.copyText')}
                            onClick={copyText} testId="grocery-share-copy-text" />
                    </div>
                    <p className="text-xs text-muted-foreground" data-testid="grocery-share-expiry">
                        {t('grocery.sharing.liveUntil', { date: until })}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="grocery-share-privacy">
                        {t('grocery.sharing.privacy')}
                    </p>
                    <button onClick={stop} disabled={busy} data-testid="grocery-share-stop"
                        className="flex min-h-[44px] items-center gap-2 text-sm font-bold text-rose-600 disabled:opacity-50 dark:text-rose-400">
                        <ShieldOff className="h-4 w-4" />{t('grocery.sharing.stop')}
                    </button>
                </>
            ) : null}

            {state && state.enabled && !share && !busy && !failed ? (
                <button onClick={prepare} data-testid="grocery-share-restart"
                    className="min-h-[44px] rounded-full bg-emerald-700 px-4 text-sm font-bold text-white">
                    {t('grocery.sharing.startAgain')}
                </button>
            ) : null}
        </div>
    );
}
